import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import tpIcon from "../../assets/images/tp-icon.png";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { totwService } from "../../services/totwService";
import TOTWPitch from "../../components/totw/TOTWPitch";
import TOTWList from "../../components/totw/TOTWList";
import TOTWFiltersBar from "../../components/totw/TOTWFiltersBar";
import TOTWBottomSheet from "../../components/totw/TOTWBottomSheet";
import { roundLabel } from "../../components/totw/totwUtils";

// =====================================================================
// Team of the Week (TP_TOTW_BRIEF) — Tools page.
//
// Data: the versioned read endpoints (services/totwService.js). The
// cascade arrives in ONE payload (league-only, no empty levels — brief
// data rule 3), so the whole selection logic is client-side:
//   country -> competition -> season -> rounds (sorted ascending).
// Default = latest available round of the last-chosen (persisted in
// localStorage) or first available competition. Round arrows step across
// the sorted round list of the selected season.
//
// View profile -> the EXISTING player profile route /search/player/<wyscout_id>.
// Contact (represented only) -> the EXISTING contact flow: the agent
// public profile /search/agent/<agent_account_id> (its Message button is
// the platform's canonical chat entry) — no new chat path.
// =====================================================================

const STORAGE_KEY = "totw_selection_v1";

const readStoredSelection = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") || null;
  } catch (e) {
    return null;
  }
};

const storeSelection = (selection) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        countryId: selection.countryId,
        competitionId: selection.competitionId,
        seasonId: selection.seasonId,
      }),
    );
  } catch (e) {
    /* storage unavailable — selection simply won't persist */
  }
};

// Resolve a (possibly stale) stored selection against the live cascade:
// every invalid level falls back to the first/newest available one, the
// round always defaults to the LATEST available (brief: newest round of the
// last-chosen or first available competition; never an empty screen).
const resolveSelection = (countries, stored) => {
  if (!countries.length) return null;
  const country =
    countries.find((c) => c.id === (stored?.countryId ?? null)) || countries[0];
  const competitions = country.competitions || [];
  const competition =
    competitions.find((c) => c.id === stored?.competitionId) || competitions[0];
  const seasons = competition?.seasons || [];
  const season = seasons.find((s) => s.id === stored?.seasonId) || seasons[0];
  const rounds = season?.rounds || [];
  const round = rounds.length ? rounds[rounds.length - 1] : null;
  return {
    countryId: country.id,
    competitionId: competition?.id || null,
    seasonId: season?.id || null,
    roundUuid: round?.uuid || null,
  };
};

const TeamOfTheWeek = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [filtersLoading, setFiltersLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [selection, setSelection] = useState(null);
  const [lineup, setLineup] = useState(null);
  const [lineupLoading, setLineupLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // ── cascade ──────────────────────────────────────────────────────────
  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    setError(null);
    const res = await totwService.getFilters();
    if (res.success) {
      const list = res.data?.countries || [];
      setCountries(list);
      setSelection(resolveSelection(list, readStoredSelection()));
      if (!list.length) {
        // No lineup data at all (e.g. before the first sync) — the brief's
        // "no empty screen" rule can't apply without data; show the error
        // panel with a retry instead of a dead pitch.
        setError("empty");
        setLineupLoading(false);
      }
    } else {
      setError("load");
      setLineupLoading(false);
    }
    setFiltersLoading(false);
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  // ── lineup ───────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    if (!selection?.roundUuid) return undefined;
    setLineupLoading(true);
    setActiveId(null);
    totwService.getLineup(selection.roundUuid).then((res) => {
      if (!alive) return;
      if (res.success) {
        setLineup(res.data);
        setError(null);
      } else {
        setError("load");
      }
      setLineupLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [selection?.roundUuid]);

  useEffect(() => {
    if (selection) storeSelection(selection);
  }, [selection]);

  const country = useMemo(
    () => countries.find((c) => c.id === selection?.countryId) || null,
    [countries, selection?.countryId],
  );
  const competition = useMemo(
    () => (country?.competitions || []).find((c) => c.id === selection?.competitionId) || null,
    [country, selection?.competitionId],
  );
  const season = useMemo(
    () => (competition?.seasons || []).find((s) => s.id === selection?.seasonId) || null,
    [competition, selection?.seasonId],
  );
  const rounds = season?.rounds || [];
  const currentRound = rounds.find((r) => r.uuid === selection?.roundUuid) || null;

  // ── selection handlers (each level resets the deeper levels) ─────────
  const pickCountry = (picked) =>
    setSelection(resolveSelection(countries, { countryId: picked.id }));
  const pickCompetition = (picked) =>
    setSelection(
      resolveSelection(countries, { countryId: selection.countryId, competitionId: picked.id }),
    );
  const pickSeason = (picked) =>
    setSelection(
      resolveSelection(countries, {
        countryId: selection.countryId,
        competitionId: selection.competitionId,
        seasonId: picked.id,
      }),
    );
  const pickRound = (picked) => {
    setSelection((prev) => ({ ...prev, roundUuid: picked.uuid }));
    setOpenMenu(null);
  };
  const stepRound = (delta) => {
    const index = rounds.findIndex((r) => r.uuid === selection?.roundUuid);
    const next = rounds[index + delta];
    if (next) setSelection((prev) => ({ ...prev, roundUuid: next.uuid }));
  };

  const toggleMenu = (name) => {
    setOpenMenu((prev) => (prev === name ? null : name));
    setActiveId(null);
  };
  const closeAll = () => {
    setOpenMenu(null);
    setActiveId(null);
  };

  // ── player actions ───────────────────────────────────────────────────
  const selectPlayer = (player) =>
    setActiveId((prev) => (prev === player.ts_player_id ? null : player.ts_player_id));
  const hoverPlayer = (player) => setActiveId(player.ts_player_id);
  const viewProfile = (player) => {
    if (player.wyscout_id != null) navigate(`/search/player/${player.wyscout_id}`);
  };
  const contactAgent = (player) => {
    if (player.agent_account_id != null) navigate(`/search/agent/${player.agent_account_id}`);
  };

  const players = lineup?.players || [];
  const loading = filtersLoading || lineupLoading;
  const subtitleRound = currentRound
    ? roundLabel(currentRound)
    : lineup
      ? roundLabel({ round: lineup.round, name: lineup.name })
      : "";

  // ── error / pre-sync empty panel ─────────────────────────────────────
  if (!loading && error) {
    return (
      <div className="min-h-screen bg-[#F7F9FB]" style={{ padding: isMobile ? "14px" : "22px 26px 28px" }}>
        <div className="mx-auto" style={{ maxWidth: 1364 }}>
          <PageHeading subtitle="" isMobile={isMobile} />
          <div className="bg-white border border-[#E9EEF4] rounded-2xl flex flex-col items-center text-center" style={{ padding: "48px 24px", gap: 12 }}>
            <div className="font-extrabold text-[#0F172A]" style={{ fontSize: 16 }}>
              Could not load the Team of the Week.
            </div>
            <button
              type="button"
              onClick={loadFilters}
              className="bg-[#2283FF] hover:bg-[#0B6BE0] text-white font-extrabold rounded-[10px] cursor-pointer"
              style={{ padding: "10px 22px", fontSize: 13 }}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9FB]" style={{ padding: isMobile ? 0 : "22px 26px 28px" }} onClick={closeAll}>
      <div className="mx-auto" style={{ maxWidth: 1364 }}>
        {isMobile ? (
          <>
            <div className="bg-white border-b border-[#EDF1F6]" style={{ padding: "14px 14px 12px" }}>
              <PageHeading subtitle={subtitleRound} isMobile competitionName={competition?.name} seasonLabel={season?.year || ""} />
              <div className="flex items-stretch gap-2" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSheetOpen(true);
                  }}
                  className="flex items-center gap-[7px] bg-white border border-[#E2E8F0] rounded-[11px] font-bold cursor-pointer text-[#0F172A]"
                  style={{ height: 42, padding: "0 13px", fontSize: 13 }}
                >
                  <span className="flex flex-col" style={{ gap: 2.5 }}>
                    <span className="block bg-[#334155] rounded-[2px]" style={{ width: 13, height: 1.8 }} />
                    <span className="block bg-[#334155] rounded-[2px]" style={{ width: 9, height: 1.8 }} />
                    <span className="block bg-[#334155] rounded-[2px]" style={{ width: 5, height: 1.8 }} />
                  </span>
                  <span>Filters</span>
                </button>
                <div className="flex items-stretch gap-1.5 flex-1">
                  <button
                    type="button"
                    onClick={() => stepRound(-1)}
                    aria-label="Previous round"
                    className="flex items-center justify-center bg-white border border-[#E2E8F0] rounded-[11px] text-[#475569] font-extrabold cursor-pointer"
                    style={{ width: 42, fontSize: 15 }}
                  >
                    ‹
                  </button>
                  <div className="flex items-center justify-center flex-1 bg-white border border-[#E2E8F0] rounded-[11px] font-bold text-[#0F172A]" style={{ fontSize: 13 }}>
                    {subtitleRound}
                  </div>
                  <button
                    type="button"
                    onClick={() => stepRound(1)}
                    aria-label="Next round"
                    className="flex items-center justify-center bg-white border border-[#E2E8F0] rounded-[11px] text-[#475569] font-extrabold cursor-pointer"
                    style={{ width: 42, fontSize: 15 }}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col" style={{ padding: "14px 14px 26px", gap: 14 }}>
              <div className="bg-white border border-[#E9EEF4] shadow-[0_1px_2px_rgba(15,23,42,0.05)]" style={{ borderRadius: 14, padding: 12 }}>
                <PitchHeader formation={lineup?.formation} loading={loading} mobile />
                <TOTWPitch
                  players={players}
                  loading={loading}
                  mobile
                  activeId={activeId}
                  onSelect={selectPlayer}
                  onViewProfile={viewProfile}
                  onContact={contactAgent}
                />
              </div>
              {/* Rows navigate straight to the player profile (Marin finding
                  5) — matched players only; unmatched rows are inert. */}
              <TOTWList players={players} loading={loading} mobile onViewProfile={viewProfile} />
            </div>
            <TOTWBottomSheet
              open={sheetOpen}
              countries={countries}
              selection={selection || {}}
              rounds={rounds}
              onPickCountry={pickCountry}
              onPickCompetition={pickCompetition}
              onPickSeason={pickSeason}
              onPickRound={pickRound}
              onReset={() => setSelection(resolveSelection(countries, null))}
              onClose={() => setSheetOpen(false)}
            />
          </>
        ) : (
          <>
            <div className="flex items-end justify-between gap-8 flex-wrap" style={{ marginBottom: 22 }}>
              <PageHeading subtitle={subtitleRound} competitionName={competition?.name} seasonLabel={season?.year || ""} />
              <TOTWFiltersBar
                countries={countries}
                selection={selection || {}}
                rounds={rounds}
                openMenu={openMenu}
                onToggleMenu={toggleMenu}
                onPickCountry={(c) => {
                  pickCountry(c);
                  setOpenMenu(null);
                }}
                onPickCompetition={(c) => {
                  pickCompetition(c);
                  setOpenMenu(null);
                }}
                onPickSeason={(s) => {
                  pickSeason(s);
                  setOpenMenu(null);
                }}
                onPickRound={pickRound}
                onStepRound={stepRound}
              />
            </div>
            <div className="grid items-start" style={{ gridTemplateColumns: "620px minmax(0, 1fr)", gap: 20 }}>
              <div className="bg-white border border-[#E9EEF4] shadow-[0_1px_2px_rgba(15,23,42,0.05)]" style={{ borderRadius: 16, padding: 18 }}>
                <PitchHeader formation={lineup?.formation} loading={loading} />
                <TOTWPitch
                  players={players}
                  loading={loading}
                  activeId={activeId}
                  onSelect={selectPlayer}
                  onHover={hoverPlayer}
                  onLeave={() => setActiveId(null)}
                  onViewProfile={viewProfile}
                  onContact={contactAgent}
                />
              </div>
              <TOTWList players={players} loading={loading} onViewProfile={viewProfile} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PageHeading = ({ subtitle, isMobile, competitionName, seasonLabel }) => (
  <div className="flex flex-col" style={{ gap: isMobile ? 4 : 6 }}>
    {!isMobile && (
      <div className="flex items-center gap-1.5 font-semibold text-[#94A3B8]" style={{ fontSize: 12 }}>
        <span>Tools</span>
        <span>/</span>
        <span className="text-[#64748B]">Team of the Week</span>
      </div>
    )}
    <h1 className="m-0 font-extrabold text-[#0F172A]" style={{ fontSize: isMobile ? 21 : 30, letterSpacing: "-0.02em" }}>
      Team of the Week
    </h1>
    {(competitionName || subtitle || seasonLabel) && (
      <div className="flex items-center gap-2 font-semibold text-[#64748B] flex-wrap" style={{ fontSize: isMobile ? 12.5 : 14 }}>
        {competitionName && <span data-no-translate="true">{competitionName}</span>}
        {competitionName && subtitle && <span className="text-[#CBD5E1]">·</span>}
        {subtitle && <span>{subtitle}</span>}
        {seasonLabel && (subtitle || competitionName) && <span className="text-[#CBD5E1]">·</span>}
        {seasonLabel && <span data-no-translate="true">{seasonLabel}</span>}
      </div>
    )}
  </div>
);

const PitchHeader = ({ formation, loading, mobile }) => (
  <div className="flex items-center justify-between" style={{ marginBottom: mobile ? 10 : 14 }}>
    <div className="flex items-center" style={{ gap: mobile ? 7 : 8 }}>
      {!loading && formation && (
        <span data-no-translate="true" className="bg-[#0F172A] text-white font-extrabold rounded-md" style={{ fontSize: mobile ? 11 : 12, letterSpacing: "0.04em", padding: mobile ? "3px 7px" : "4px 8px" }}>
          {formation}
        </span>
      )}
      {!mobile && !loading && formation && (
        <span className="font-semibold text-[#64748B]" style={{ fontSize: 12.5 }}>
          Formation
        </span>
      )}
    </div>
    <div className="flex items-center font-bold text-[#1257B0] bg-[#EAF3FF] rounded-full" style={{ gap: mobile ? 5 : 6, fontSize: mobile ? 11 : 12, padding: mobile ? "4px 9px 4px 6px" : "5px 10px 5px 7px" }}>
      <img src={tpIcon} alt="TransferPitch" className="block" style={{ width: mobile ? 16 : 18, height: mobile ? 16 : 18 }} />
      <span>On TransferPitch</span>
    </div>
  </div>
);

export default TeamOfTheWeek;
